import {
  CalendarDays,
  Eye,
  Layers,
  MapPin,
  MonitorPlay,
  Play,
  Timer,
  Tv,
} from 'lucide-react';
import type {
  CreativePreview,
  ManualCampaignForm,
  PreviewData,
} from '@/features/campaigns/types/campaign';
import { ContentPreviewMedia } from '@/features/campaigns/components/ContentPreviewMedia';
import { formatCount } from '@/shared/lib/format/money';
import { resolveAnalyticsSlotCount } from '../previewBilling';

type OverviewSectionProps = {
  data: PreviewData;
  campaignData: ManualCampaignForm;
  creativePreview: CreativePreview;
  analytics: Record<string, unknown>;
  screenCount: number;
  bookingSlotCount?: number;
  onOpenPreview?: () => void;
};

function parseDay(value?: string): Date | null {
  if (!value) return null;
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDay(d: Date | null, withYear = true) {
  if (!d) return '—';
  return d.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

function formatAirtime(totalSeconds: number) {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '—';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.round(totalSeconds % 60);
  const parts = [h ? `${h}h` : '', m ? `${m}m` : '', s || (!h && !m) ? `${s}s` : ''];
  return parts.filter(Boolean).join(' ');
}

function Tile({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="min-w-0 rounded-xl border border-[var(--dash-line)] bg-white px-3 py-2.5">
      <p className="m-0 flex items-center gap-1.5 text-[10px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
        <span className="text-[var(--dash-brand)]">{icon}</span>
        {label}
      </p>
      <p className="m-0 mt-1 truncate text-[18px] leading-none font-bold tracking-tight text-[var(--dash-ink)] tabular-nums">
        {value}
      </p>
      {hint ? (
        <p className="m-0 mt-1 truncate text-[11px] text-[var(--dash-muted)]">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function OverviewSection({
  data,
  campaignData,
  creativePreview,
  analytics,
  screenCount,
  bookingSlotCount,
  onOpenPreview,
}: OverviewSectionProps) {
  const hasContent =
    Boolean(creativePreview.url) || Boolean(campaignData.mediaIds?.[0]?.mediaId);
  const campaignName =
    campaignData.campaignName || data.campaignName || 'Untitled campaign';
  const goal = campaignData.campaignGoals || data.campaignGoals || '';
  const cities = (
    campaignData.targetCities?.length
      ? campaignData.targetCities
      : data.targetCities || []
  ).filter(Boolean);

  const start = parseDay(campaignData.startDate || data.startDate);
  const end = parseDay(campaignData.endDate || data.endDate);
  const days =
    start && end
      ? Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1)
      : null;

  const durationValue =
    campaignData.customDuration ||
    data.customDuration ||
    analytics.customDuration;
  const playSeconds = Number(durationValue) || 0;
  const slotCount = resolveAnalyticsSlotCount(
    analytics,
    bookingSlotCount,
    Array.isArray(data.bookedSlots) ? data.bookedSlots : null,
  );
  const totalPlays = Number(analytics.totalRepetitions);
  const totalAirtime = Number(analytics.totalDuration);
  const mediaType = String(
    creativePreview.type ||
      campaignData.mediaType ||
      campaignData.mediaIds?.[0]?.mediaType ||
      'Creative',
  )
    .replace(/^image\/.+/i, 'Image')
    .replace(/^video\/.+/i, 'Video');

  return (
    <section className="camp-section overflow-hidden text-left !p-0">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--dash-line)] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <MonitorPlay className="h-4 w-4 text-[var(--dash-brand)]" />
          <h2 className="m-0 text-[13px] font-semibold text-[var(--dash-ink)]">
            Campaign overview
          </h2>
        </div>
        {onOpenPreview ? (
          <button
            type="button"
            onClick={onOpenPreview}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--dash-line)] bg-white px-2.5 py-1 text-[11.5px] font-semibold text-[var(--dash-ink)] transition hover:bg-[var(--dash-soft)]"
          >
            <Eye className="h-3.5 w-3.5" />
            View preview
          </button>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 p-4 sm:flex-row">
        <div className="flex shrink-0 flex-row items-center gap-3 sm:w-[132px] sm:flex-col sm:items-stretch">
          <div className="flex h-[132px] w-[96px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[var(--dash-line)] bg-[#0B1220] sm:h-[150px] sm:w-full">
            {hasContent && creativePreview.url ? (
              <ContentPreviewMedia
                url={creativePreview.url}
                type={
                  creativePreview.type ||
                  campaignData.mediaType ||
                  campaignData.mediaIds?.[0]?.mediaType ||
                  ''
                }
                className="h-full w-full object-contain"
              />
            ) : (
              <span className="px-2 text-center text-[11px] text-white/50">
                No creative
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 sm:justify-center">
            <span className="rounded-full bg-[var(--dash-soft)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--dash-ink)]">
              {mediaType}
            </span>
            {playSeconds ? (
              <span className="rounded-full bg-[var(--dash-soft)] px-2 py-0.5 text-[10.5px] font-semibold text-[var(--dash-muted)]">
                {playSeconds}s / play
              </span>
            ) : null}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3
              className="m-0 min-w-0 truncate text-[18px] font-bold tracking-tight text-[var(--dash-ink)]"
              title={campaignName}
            >
              {campaignName}
            </h3>
            {goal ? (
              <span className="rounded-full bg-[var(--dash-brand)]/10 px-2.5 py-0.5 text-[10.5px] font-semibold tracking-wide text-[var(--dash-brand)] uppercase">
                {goal}
              </span>
            ) : null}
          </div>

          <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
            <div className="flex items-start gap-2 rounded-xl bg-[var(--dash-soft)] px-3 py-2">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-[var(--dash-brand)]" />
              <div className="min-w-0">
                <p className="m-0 text-[10px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                  Campaign dates
                </p>
                <p className="m-0 text-[13px] font-semibold text-[var(--dash-ink)]">
                  {formatDay(start)} – {formatDay(end)}
                </p>
                {days ? (
                  <p className="m-0 text-[11px] text-[var(--dash-muted)]">
                    {days} day{days === 1 ? '' : 's'}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex items-start gap-2 rounded-xl bg-[var(--dash-soft)] px-3 py-2">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--dash-brand)]" />
              <div className="min-w-0">
                <p className="m-0 text-[10px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                  Target cities
                </p>
                {cities.length ? (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {cities.map((city) => (
                      <span
                        key={city}
                        className="rounded-full bg-white px-2 py-0.5 text-[11.5px] font-semibold text-[var(--dash-ink)] ring-1 ring-black/[0.06]"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="m-0 text-[13px] font-semibold text-[var(--dash-ink)]">
                    —
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="mt-2.5 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <Tile
              icon={<Tv className="h-3.5 w-3.5" />}
              label="Screens"
              value={screenCount ? formatCount(screenCount) : '—'}
            />
            <Tile
              icon={<Play className="h-3.5 w-3.5" />}
              label="Total plays"
              value={Number.isFinite(totalPlays) && analytics.totalRepetitions != null ? formatCount(totalPlays) : '—'}
              hint={
                screenCount && Number.isFinite(totalPlays) && totalPlays > 0
                  ? `≈ ${formatCount(Math.round(totalPlays / screenCount))} per screen`
                  : undefined
              }
            />
            <Tile
              icon={<Layers className="h-3.5 w-3.5" />}
              label="Booked slots"
              value={slotCount != null ? formatCount(slotCount) : '—'}
              hint="Unique date & hour"
            />
            <Tile
              icon={<Timer className="h-3.5 w-3.5" />}
              label="Total airtime"
              value={formatAirtime(totalAirtime)}
              hint={playSeconds ? `${playSeconds}s × plays` : undefined}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
